import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { brand, hero, heartSection, services, sectors, testimonials, news } from '../mock/mockData';
import { useContent } from '../context/ContentContext';

export default function Home() {
  const [activeService, setActiveService] = useState(0);
  const { content } = useContent();
  const c = (key, fallback) => (content && content[key] ? content[key] : fallback);

  return (
    <div className="pt-24">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={hero.image} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-forest-950/85 via-forest-900/70 to-forest-900" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-24">
          <p className="text-sage-400 tracking-[0.3em] text-xs md:text-sm uppercase mb-6 reveal">{c('hero_eyebrow', hero.eyebrow)}</p>
          <h1 className="font-serif text-cream-50 text-5xl md:text-7xl lg:text-8xl leading-[1.05] max-w-4xl reveal" style={{transitionDelay: '120ms'}}>{c('hero_title', hero.title)}</h1>
          <p className="mt-8 text-cream-100/80 text-lg md:text-xl max-w-2xl leading-relaxed reveal" style={{transitionDelay: '240ms'}}>{c('hero_intro', hero.intro)}</p>
          <div className="mt-10 flex flex-wrap gap-4 reveal" style={{transitionDelay: '360ms'}}>
            <Link to={hero.ctaHref} className="group inline-flex items-center gap-3 bg-sage-500 text-forest-900 px-8 py-4 text-sm font-medium tracking-wide hover:bg-sage-600 transition-all duration-300">
              {c('hero_cta_label', hero.ctaLabel)} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/services" className="group inline-flex items-center gap-3 border border-cream-100/30 text-cream-100 px-8 py-4 text-sm font-medium tracking-wide hover:border-sage-400 hover:text-sage-400 transition-all duration-300">
              Explore Our Services <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO STATEMENT */}
      <section className="bg-forest-900 py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <p className="text-sage-400 tracking-[0.3em] text-xs uppercase mb-6 reveal">Solicitors in Harpenden</p>
          <h2 className="font-serif text-cream-50 text-4xl md:text-5xl lg:text-6xl leading-tight reveal" style={{transitionDelay:'100ms'}}>{c('intro_section_title', 'Legal Services with Care')}</h2>
          <p className="mt-8 text-cream-100/75 text-lg leading-relaxed reveal" style={{transitionDelay:'200ms'}}>
            {c('intro_section_body', `At ${brand.name}, we provide expert legal services to Housing Associations, Developers, Local Authorities, large and small corporate businesses, as well as residential buyers and sellers. As established solicitors in Harpenden, we combine technical expertise with a practical, client-focused approach. Our team supports clients throughout the life of their projects and cases, from initial planning through to completion or resolution.`)}
          </p>
        </div>
      </section>

      {/* SERVICES – tabbed like Hedges */}
      <section className="bg-forest-950 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <h2 className="section-title font-serif text-cream-50 text-4xl md:text-5xl reveal">Our Services</h2>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-b border-forest-800 pb-4">
            {services.map((s, i) => (
              <button key={s.slug} onClick={() => setActiveService(i)} className={`text-sm md:text-base tracking-wide transition-colors ${activeService === i ? 'text-sage-400' : 'text-cream-100/70 hover:text-cream-50'}`}>{s.name}</button>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center reveal">
            <div className="aspect-[4/3] overflow-hidden bg-forest-800">
              <img key={services[activeService].slug} src={services[activeService].image} alt={services[activeService].name} className="w-full h-full object-cover animate-fade-up" />
            </div>
            <div>
              <h3 className="font-serif text-cream-50 text-3xl md:text-4xl mb-5">{services[activeService].name}</h3>
              <p className="text-cream-100/80 leading-relaxed text-base md:text-lg">{services[activeService].intro}</p>
              <ul className="mt-6 space-y-2">
                {services[activeService].highlights.slice(0, 4).map((h) => (
                  <li key={h} className="flex gap-3 text-cream-100/75 text-[15px]"><span className="text-sage-400 mt-1">•</span>{h}</li>
                ))}
              </ul>
              <Link to={`/services/${services[activeService].slug}`} className="mt-8 inline-flex items-center gap-2 text-sage-400 border-b border-sage-400/40 pb-1 hover:border-sage-400 transition-colors">
                All {services[activeService].name} services <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTORS MARQUEE-STYLE GRID */}
      <section className="bg-forest-900 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <h2 className="section-title font-serif text-cream-50 text-4xl md:text-5xl reveal">Sectors</h2>
            <p className="text-cream-100/70 max-w-md text-sm md:text-base reveal">Experience across housing, property, retail, professional services and more.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {sectors.map((s, i) => {
              const Tile = s.href ? Link : 'div';
              return (
              <Tile key={s.name} {...(s.href ? { to: s.href, 'data-testid': `sector-link-${s.href.split('/').pop()}` } : {})} className="group relative block aspect-square overflow-hidden bg-forest-800 animate-fade-up" style={{animationDelay: `${Math.min(i, 8) * 60}ms`}}>
                <img
                  src={s.image}
                  alt={s.name}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-950/95 via-forest-950/40 to-transparent" />
                <div className="absolute inset-0 p-5 flex items-end">
                  <h4 className="font-serif text-cream-50 text-lg md:text-xl leading-tight">{s.name}</h4>
                </div>
              </Tile>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-forest-950 py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <h2 className="section-title font-serif text-cream-50 text-4xl md:text-5xl reveal">What Our Clients Say</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((t, i) => (
              <blockquote key={t.author} className="border-l-2 border-sage-500 pl-6 py-2 reveal" style={{transitionDelay: `${i * 80}ms`}}>
                <p className="text-cream-100/85 font-serif text-xl md:text-2xl leading-relaxed italic">“{t.quote}”</p>
                <footer className="mt-4 text-sage-400 text-xs tracking-[0.25em] uppercase">{t.author}</footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* HEART OF PM */}
      <section className="bg-forest-900 py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="aspect-[4/5] overflow-hidden bg-forest-800 reveal">
            <img src={heartSection.image} alt="Harpenden countryside" className="w-full h-full object-cover" />
          </div>
          <div className="reveal" style={{transitionDelay:'120ms'}}>
            <h2 className="font-serif text-cream-50 text-4xl md:text-5xl leading-tight">{c('heart_title', heartSection.title)}</h2>
            <p className="mt-6 text-cream-100/80 leading-relaxed text-base md:text-lg">{c('heart_body', heartSection.body)}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/about" className="group inline-flex items-center gap-3 border border-cream-100/30 text-cream-100 px-7 py-3 text-sm tracking-wide hover:border-sage-400 hover:text-sage-400 transition-colors">
                About Us <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/our-people" className="group inline-flex items-center gap-3 bg-sage-500 text-forest-900 px-7 py-3 text-sm font-medium tracking-wide hover:bg-sage-600 transition-colors">
                Meet our Team <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NEWS */}
      <section className="bg-forest-950 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <h2 className="section-title font-serif text-cream-50 text-4xl md:text-5xl reveal">Insights from Perrin Myddelton</h2>
            <Link to="/insights" className="text-sage-400 text-sm border-b border-sage-400/40 pb-1 hover:border-sage-400">View all articles</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {news.map((n, i) => (
              <article key={n.title} className="group bg-forest-900 border border-forest-800 hover:border-sage-500/60 transition-colors p-6 reveal" style={{transitionDelay: `${i * 80}ms`}}>
                <p className="text-sage-400 text-[11px] tracking-[0.25em] uppercase">{n.tag}</p>
                <h3 className="mt-3 font-serif text-cream-50 text-xl leading-snug group-hover:text-sage-400 transition-colors">{n.title}</h3>
                <p className="mt-3 text-cream-100/70 text-sm leading-relaxed">{n.excerpt}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sage-400 text-xs tracking-widest uppercase">Read More <ArrowRight className="h-3.5 w-3.5" /></span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-forest-800 py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <h3 className="font-serif text-cream-50 text-3xl md:text-4xl">Have a legal matter you'd like to discuss?</h3>
          <p className="mt-4 text-cream-100/75">Get in touch with our team and we'll get back to you promptly.</p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-3 bg-sage-500 text-forest-900 px-8 py-4 text-sm font-medium tracking-wide hover:bg-sage-600 transition-colors">
            Contact Us <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
